/**
 * Karta oszczędności — % przychodów oszczędzonych w wybranym zakresie.
 * Ring progress + delta vs poprzedni okres.
 */
import { formatCurrency } from '../../lib/utils';
import { Icons } from '../Icons';

interface Props {
  income: number;
  expense: number;
  rate: number; // 0-100
}

export function SavingsCard({ income, expense, rate }: Props) {
  const saved = Math.max(0, income - expense);
  const ringR = 28;
  const circ = 2 * Math.PI * ringR;
  const offset = circ - (rate / 100) * circ;

  const tone =
    rate >= 30
      ? { text: 'text-violet-600 dark:text-violet-300', stroke: 'stroke-violet-500', label: 'Świetnie' }
      : rate >= 15
      ? { text: 'text-sky-600 dark:text-sky-300', stroke: 'stroke-sky-500', label: 'OK' }
      : { text: 'text-amber-600 dark:text-amber-300', stroke: 'stroke-amber-500', label: 'Warto poprawić' };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-violet-100 dark:bg-violet-500/15 flex items-center justify-center">
          <Icons.PiggyBank size={18} className="text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 font-bold">Oszczędności</p>
          <p className={`text-sm font-semibold ${tone.text}`}>{tone.label}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 shrink-0">
          <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
            <circle cx="32" cy="32" r={ringR} fill="none" strokeWidth="6" className="stroke-slate-200 dark:stroke-slate-700" />
            <circle
              cx="32"
              cy="32"
              r={ringR}
              fill="none"
              strokeWidth="6"
              strokeLinecap="round"
              className={tone.stroke}
              strokeDasharray={circ}
              strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 0.6s cubic-bezier(0.16,1,0.3,1)' }}
            />
          </svg>
          <div className={`absolute inset-0 flex items-center justify-center text-base font-bold tabular-nums ${tone.text}`}>
            {rate}%
          </div>
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">Przychody</span>
            <span className="font-bold tabular-nums text-emerald-600 dark:text-emerald-300">+{formatCurrency(income)}</span>
          </div>
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">Wydatki</span>
            <span className="font-bold tabular-nums text-rose-600 dark:text-rose-300">-{formatCurrency(expense)}</span>
          </div>
          <div className="flex items-baseline justify-between text-sm pt-1.5 border-t border-slate-100 dark:border-slate-700">
            <span className="text-slate-700 dark:text-slate-200 font-semibold">Zaoszczędzono</span>
            <span className={`font-bold tabular-nums ${tone.text}`}>{formatCurrency(saved)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
